import * as quotes from '../xhr/quotes.js';
import * as types from './types/quotes.js';
import * as notifier from './notifier.js';
import store from '../redux/store.js';
import * as loader from './loader.js';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 * @param state
 * @param quoteID
 */
export function setState(state, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return quotes
            .setState(state, quoteID)
            .then(() => {
                dispatch({
                    type: types.SET_STATE,
                    state,
                    quoteID
                });

                setLoader(dispatch, false);
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 *
 * @param {*} query
 * @param {*} nextCursor
 */
export function getQuotesForReview(query, nextCursor = false) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().quotes.forReview.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        return quotes
            .getQuotesForReview(cursor, query)
            .then((data) => {
                dispatch({
                    type: types.GET_QUOTES_FOR_REVIEW,
                    quotes: data.quotes,
                    cursor: data.quotes.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param advisorID
 * @param body
 * @returns {function(*=): Promise<unknown>}
 */
export function createQuote(advisorID, body) {
    return (dispatch) => {
        setLoader(dispatch, true, 'modal');

        return quotes.createQuote(advisorID, body)
            .then((data) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Successfully created your quote.'));

                setTimeout(() => {
                    window.location = `/advisors/${ advisorID }/quotes/${ data.quoteID }`;
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param quoteID
 * @param body
 * @returns {function(*=): (Promise<any> | * | undefined | Promise<void>)}
 */
export function editQuote(quoteID, body) {
    return (dispatch) => {
        setLoader(dispatch, true, 'modal');

        return quotes.editQuote(quoteID, body)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Successfully edited your quote.'));

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function getQuote(quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return quotes.getQuote(quoteID)
            .then((data) => {
                dispatch({
                    type: types.GET_QUOTE_SUCCESS,
                    data
                });

                setLoader(dispatch, false);
                return data.meta;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param planType
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function savePlanType(planType, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return quotes.savePlanType(planType, quoteID)
            .then(() => {
                dispatch({
                    type: types.SET_PLAN_TYPE,
                    planType,
                    quoteID
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function sendReviewRequest(quoteID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return quotes.sendReviewRequest(quoteID)
            .then(() => {
                dispatch({
                    type: types.SET_STATE,
                    state: 4,
                    quoteID
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param coverages
 * @param quoteID
 */
export function savePricingCoverages(classID, coverages, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return quotes.savePricingCoverages(classID, coverages, quoteID)
            .then(() => {
                dispatch({
                    type: types.SAVE_CLASS_COVERAGES,
                    classID,
                    coverages
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param quoteID
 */
export function convertQuote(quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true, 'modal');
        return quotes.convertQuote(quoteID)
            .then((accountID) => {
                dispatch(notifier.displayMessage(`Quote successfully converted!`));
                window.location.href = `/accounts/${ accountID }`;

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param classID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function removeClass(classID, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return quotes
            .removeClass(classID, quoteID)
            .then(() => {
                dispatch({
                    type: types.REMOVE_CLASS_SUCCESS,
                    data: {
                        quoteID,
                        classID
                    }
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Class removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param divisionID
 * @param quoteID
 * @returns {function(*=): Promise<boolean>}
 */
export function removeDivision(divisionID, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return quotes
            .removeDivision(divisionID, quoteID)
            .then(() => {
                dispatch({
                    type: types.REMOVE_DIVISION_SUCCESS,
                    data: {
                        quoteID,
                        divisionID
                    }
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Division removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param quoteID
 * @param commissionSplitData
 * @returns {function(*): Promise<*>}
 */
export function updateCommissionSplitsForQuote(commissionSplitData, quoteID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return quotes.updateCommissionSplitsForQuote(quoteID, commissionSplitData)
            .then((data) => {
                setLoader(dispatch, false);

                dispatch({
                    type: types.EDIT_COMMISSION_SPLIT_SUCCESS,
                    data
                });

                return data.commissionSplits;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}